import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import UserContext from '../../../context/User/User'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { usePagination } from '../../../providers/PaginationProvider/PaginationProvider'

import useDebouncedValue from '../../../utilities/useDebouncedValue'
import useAbortableFetch from '../../../utilities/useAbortableFetch'

import AuthService from '../../../utilities/services/auth.service'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import { unifiedSearch } from '../../../utilities/api'

import {
  orderStatuses,
  formatNumber,
  checkPermission,
  exportData,
  formatTicketLocationValue,
} from '../../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'

import { NoPermissionsContainer } from '../../NoPermissionsContainer'
import { PageLoadingContainer } from '../../PageLoadingContainer'
import { LoadingContainer } from '../../LoadingContainer'
import { EmptyContainer } from '../../EmptyContainer'
import { Pagination } from '../../Pagination'
import { OrderSummary } from '../../SearchOrdersWrapper/OrdersList/OrderSummary'
import { OrderInfo } from '../../SearchOrdersWrapper/OrdersList/OrdersList'
import { SearchHeader } from '../SearchHeader'
import Button from 'react-bootstrap/Button'
import { BulkSendReservedPackageInvoices } from '../../ModalContent/BulkSendReservedPackageInvoices'

// Match season-packages Package Holders status filter options
const HIDDEN_STATUSES = [
  'incomplete',
  'failed',
  'pending_send',
  'released',
  'refunded',
  'reserved',
]

const statusOpts = [
  {
    label: 'All',
    value: 'all',
  },
  ...[
    ...new Map(
      Object.values(orderStatuses)
        .filter((obj) => !HIDDEN_STATUSES.includes(obj.value))
        .map((obj) => [obj.value, { label: obj.label, value: obj.value }])
    ).values(),
  ],
]

const COMPLETE_ORDER_STATUSES = new Set([
  'complete',
  'completeFromAssignment',
  'completeFromExchange',
  'completeFromTransfer',
  'completeFromGroupPurchase',
  'completeFromInvoice',
])

const formatHolderSeats = (order) => {
  const tickets = order?.package_tickets?.length
    ? order.package_tickets
    : order?.tickets || []

  return [
    ...new Set(
      tickets
        .filter(Boolean)
        .map((ticket) => {
          const isGeneralAdmission = ticket?.GA || ticket?.generalAdmission
          const fallback = isGeneralAdmission ? 'GA' : ''

          return [
            formatTicketLocationValue(ticket?.sectionNumber, fallback),
            formatTicketLocationValue(ticket?.rowNumber, fallback),
            formatTicketLocationValue(ticket?.seatNumber, fallback),
          ]
            .filter((part) => part != null && String(part).trim() !== '')
            .join('-')
        })
        .filter(Boolean)
    ),
  ].join(', ')
}

const formatHolderAddress = (order) => {
  if (order?.address) return order.address

  const profileAddress = order?.users_permissions_user?.profile?.address
  const address = Array.isArray(profileAddress)
    ? profileAddress[0]
    : profileAddress
  if (!address) return ''

  return [
    address.address_1,
    address.address_2,
    [address.city, address.state, address.zipcode].filter(Boolean).join(' '),
    address.country,
  ]
    .filter(Boolean)
    .join(', ')
}

const isComplimentaryHolder = (order) => {
  const revenue = parseFloat(order?.total ?? order?.gross ?? 0) || 0
  if (revenue > 0) return false
  return (
    order?.assignmentType === 'comp' ||
    order?.assignment_type === 'comp' ||
    order?.complimentary === true ||
    order?.paymentMethodType === 'comp'
  )
}

const countHolderSeats = (order) => {
  const tickets = order?.package_tickets?.length
    ? order.package_tickets
    : order?.tickets || []
  return tickets.filter(Boolean).length
}

const toSeasonHolderExportRow = (order) => {
  const complimentary = isComplimentaryHolder(order)
  return {
    seats: formatHolderSeats(order),
    seat_count: countHolderSeats(order),
    number_of_seats: countHolderSeats(order),
    number_of_tickets: order?.originalTickets?.length ?? 0,
    package_name: order?.package?.name || '',
    first_name:
      order?.firstName || order?.users_permissions_user?.firstName || '',
    last_name: order?.lastName || order?.users_permissions_user?.lastName || '',
    email: order?.email || order?.users_permissions_user?.email || '',
    phone_number:
      order?.phoneNumber || order?.users_permissions_user?.phoneNumber || '',
    address: formatHolderAddress(order),
    processed_at: order?.processedAt || order?.processed_at || '',
    date_paid: order?.datePaid || order?.date_paid || '',
    amount: COMPLETE_ORDER_STATUSES.has(order?.status)
      ? 0
      : (order?.details?.pricing?.total ?? order?.total ?? 0),
    total: order?.details?.pricing?.total ?? order?.total ?? 0,
    complimentary,
    status: complimentary ? 'comp' : order?.status || '',
  }
}

export default function PackageHolders({ uuid }) {
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.SEASON_PACKAGES_MANAGEMENT
    )

  const [orders, setOrders] = useState([])
  const [isInitialState, setIsInitialState] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [status, setStatus] = useState(statusOpts[0].value)
  const [searchTerm, setSearchTerm] = useState('')
  const [totalOrders, setTotalOrders] = useState(0)
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false)
  const didMountRef = useRef(false)
  const organization = AuthService.getOrg()

  // Hooks for convenience
  const debouncedQuery = useDebouncedValue(searchTerm, 300)
  const abortable = useAbortableFetch()

  const {
    currentPage,
    setCurrentPage,
    perPage,
    pageCount,
    setPageCount,
    resultsPerPageOpts,
    handleChange,
  } = usePagination()

  const runSearch = useCallback(
    async (isFirstTime = false) => {
      setIsLoading(true)
      try {
        /*
          uuid,
          by = "event", // event | package | both
          orderType,
          paymentStatus,
          searchTerm,
          sectionNumber,
          rowNumber,
          seatNumbers, // "1", "1,2,5", "1-8"
          page = 1,
          perPage = 25,
        */
        const res = await abortable((signal) =>
          unifiedSearch(
            {
              organizationUUID: organization?.[0]?.uuid,
              uuid: uuid,
              by: 'package',
              searchTerm: debouncedQuery,
              paymentStatus: status,
              page: currentPage + 1,
              perPage: perPage,
            },
            signal
          )
        )

        const {
          data,
          meta: {
            pagination: { pageCount: pc, total },
          },
        } = res.data

        setOrders(data)
        setPageCount(pc)
        setTotalOrders(formatNumber(total))
        setIsLoading(false)
      } catch (err) {
        if (err?.name !== 'CanceledError') {
          console.error(err)
          setIsLoading(false)
        }
      } finally {
        setIsInitialState(isFirstTime)
      }
    },
    [
      abortable,
      uuid,
      currentPage,
      perPage,
      debouncedQuery,
      status,
      setPageCount,
    ]
  )

  const handleStatusChange = (value) => {
    setStatus(value)
  }

  const handleExportAll = async () => {
    try {
      const organizationUUID = organization?.[0]?.uuid
      if (!organizationUUID || !uuid) return

      const perPageForExport = 500
      let page = 1
      let exportPageCount = 1
      const allOrders = []

      do {
        const res = await unifiedSearch({
          organizationUUID,
          uuid,
          by: 'package',
          searchTerm: debouncedQuery,
          paymentStatus: status,
          page,
          perPage: perPageForExport,
        })

        const responseData = res?.data?.data || []
        const pagination = res?.data?.meta?.pagination || {}
        exportPageCount = Number(pagination.pageCount || 1)

        allOrders.push(...responseData)
        page += 1
      } while (page <= exportPageCount)

      if (!allOrders.length) return

      exportData(
        allOrders.map(toSeasonHolderExportRow),
        'season_holders',
        undefined,
        allOrders?.[0]?.package?.name || orders?.[0]?.package?.name,
        undefined,
        organization?.[0]?.name,
        undefined,
        'package'
      )
    } catch (err) {
      console.error('Failed to export all package holders', err)
    }
  }

  useEffect(() => {
    if (!didMountRef.current) return // ⛔ skip on first render
    runSearch() // ✅ run only for real changes
  }, [debouncedQuery, status, currentPage, perPage])

  useEffect(() => {
    if (!orgPermissionsReady || !hasPermission) return
    runSearch(true) // True as in it is initial query
    didMountRef.current = true
    // run when org permissions are ready and season packages management is allowed
  }, [orgPermissionsReady, hasPermission])

  useEffect(() => {
    setCurrentPage(0)
  }, [debouncedQuery, status])

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="max-width-wrapper"
          data-testid="package-holders-section"
        >
          <Header
            organization={organization[0]}
            orders={orders}
            isInitialState={isInitialState}
            status={status}
            handleStatusChange={handleStatusChange}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onExport={handleExportAll}
            onOpenBulkInvoices={() => setShowBulkInvoiceModal(true)}
          />
          <Results
            orders={orders}
            totalOrders={totalOrders}
            isInitialState={isInitialState}
            isLoading={isLoading}
            pagination={{
              currentPage,
              setCurrentPage,
              perPage,
              pageCount,
              setPageCount,
              resultsPerPageOpts,
              handleChange,
            }}
            runSearch={runSearch}
          />
        </section>
      </div>
      <BulkSendReservedPackageInvoices
        packageUUID={uuid}
        show={showBulkInvoiceModal}
        onHide={() => setShowBulkInvoiceModal(false)}
        onSuccess={runSearch}
      />
    </>
  )
}

function Results({
  orders,
  totalOrders,
  isInitialState,
  isLoading,
  pagination,
  runSearch,
}) {
  const { openModal } = useModal()
  if (isLoading) {
    return <LoadingContainer />
  }

  // InitialState
  if (isInitialState && !orders?.length) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No package holders</p>
          <p className="subtitle">This event has no package holders.</p>
        </EmptyContainer>
      </Card>
    )
  }

  if (orders?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No results found...</p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <>
      <div className="mb-4">
        <OrderInfo isLoading={isLoading} totalOrders={totalOrders} />
      </div>
      <Stack as="ul" gap={4}>
        {orders.map((order, i) => (
          <OrderSummary
            key={i}
            order={order}
            runSearch={runSearch}
            showDatePaid
          />
        ))}
      </Stack>
      <Pagination
        pageCount={pagination.pageCount}
        perPage={pagination.perPage}
        currentPage={pagination.currentPage}
        resultsPerPageOpts={pagination.resultsPerPageOpts}
        handleClick={(value) => pagination.setCurrentPage(value.selected)}
        handleChange={pagination.handleChange}
      />
    </>
  )
}

function Header({
  orders,
  organization,
  isInitialState,
  status,
  handleStatusChange,
  setSearchTerm,
  onExport,
  onOpenBulkInvoices,
}) {
  return (
    <SearchHeader
      actionsRightComponent={null}
      rightSideComponent={
        <Button
          variant="outline-light"
          size="lg"
          onClick={onOpenBulkInvoices}
          aria-label="Send invoices to reserved season ticket holders"
        >
          Send invoices
        </Button>
      }
      selectOptions={{
        show: true,
        options: statusOpts,
        value: status,
        handleChange: handleStatusChange,
        label: 'Filter By Status',
        labelId: 'filter-by-status',
      }}
      showActions={!isInitialState || orders?.length > 0}
      title="Package holders"
      desc="Comprehensive list of customers who have purchased season packages"
      searchPlaceholder="Search by name or email"
      setSearchTerm={setSearchTerm}
      organizationName={organization?.name}
      packageName={orders[0]?.package?.name}
      downloadReport={onExport}
    />
  )
}
